import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  EventEmitter,
  Output,
  NgModule,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Document } from 'src/app/shared/models/document';
import { NotificationService } from '../../services/notification.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import {
  DxFileUploaderModule,
  DxButtonModule,
  DxPopupModule,
  DxProgressBarModule,
} from 'devextreme-angular';
import { MsTooltipModule } from '../ms-tooltip/ms-tooltip.component';
import { DocumentService } from '../../services/documet/document.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { PharmacyService } from 'src/app/shared/services/user/pharmacy.service';
import { ThisReceiver } from '@angular/compiler';
import { PosthogService } from 'src/app/shared/services/posthog.service';

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
})
export class DocumentUploadComponent implements OnInit, OnChanges {
  @Input() showDelete: boolean;
  @Input() context: any;
  @Input() listingId: number;
  @Input() companyId: string;
  @Input() facilityId: string;
  @Input() stateLicense: string;
  @Input() deaLicense: string;
  @Input() updateLicenseinDB: boolean = false;
  @Input() orderId: string;
  @Input() type: string;
  @Input() limit: number = 1;
  @Input() listingImage: string;
  @Input() listingImageUrl: string;
  @Input() email: string;
  @Input() documents: Document[] = [];
  @Input() disabled: boolean;
  @Input() preview = false;
  @Input() allowedFileExtensions = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.PDF',
    '.JPG',
    '.JPEG',
    '.PNG',
  ];
  @Input() persistUploadButton = false;
  @Input() disableUpload = false;
  @Output() update = new EventEmitter<boolean>();
  @Output() licenseModalClosed = new EventEmitter<boolean>();
  /** How many documents this uploader is rendering — tracks uploads and deletes. */
  @Output() countChange = new EventEmitter<number>();
  allowedFileExtensionsAllCases: any[] = [];

  filteredDocuments: any[] = [];

  filesToUpload: any[] = [];
  private uploadQueue: File[] = [];
  private batch = { total: 0, uploaded: 0 };
  isDragOver = false;
  @ViewChild('nativeFileInput') nativeFileInput: ElementRef<HTMLInputElement>;

  askDeleteVisible = false;

  selectedDocument: any;

  isProcessing = false;
  progressValue = 0;
  processMessage = '';
  canCloseProcessing = false;
  progressInterval: any;
  apiTimeout: any;

  constructor(
    private sanitizer: DomSanitizer,
    private documentService: DocumentService,
    private notifyService: NotificationService,
    private sessionService: SessionService,
    private pharmacyService: PharmacyService,
    private posthogService: PosthogService,
  ) {}

  ngOnInit() {
    this.getReadebleExtentions();
    this.getDocuments();
    this.filteredDocuments = [];
  }

  private getReadebleExtentions() {
    this.allowedFileExtensionsAllCases = this.allowedFileExtensions.map(
      extension => extension.toLowerCase(),
    );
    this.allowedFileExtensionsAllCases =
      this.allowedFileExtensionsAllCases.filter(function (elem, index, self) {
        return index === self.indexOf(elem);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    const facilityIdChange = changes['facilityId'];
    if (typeof facilityIdChange?.previousValue !== 'undefined') {
      this.getDocuments();
    }
  }

  getDocuments() {
    const search = {
      context: this.context,
      type: this.type,
    };

    if (this.context === 'listing' && this.type !== 'global_listing') {
      search['listing_id'] = this.listingId;
    } else if (this.context === 'listing' && this.type === 'global_listing') {
      search['company_id'] = this.companyId;
    } else if (this.context === 'company' && this.type === 'open_document') {
      search['company_id'] = this.companyId;
    } else if (this.context === 'profile') {
      search['company_id'] = this.companyId;
    } else if (this.context === 'order') {
      search['order_id'] = this.orderId;
    } else if (this.context === 'facility') {
      search['facility_id'] = this.facilityId;
      search['company_id'] = this.companyId;
    }

    this.documentService.get(search).subscribe(
      result => {
        this.documents = result.data;
        this.disabled =
          this.documents.length &&
          this.context === 'facility' &&
          ['terms_pdf', 'state_license', 'reseller_certificate'].includes(
            this.type,
          );
        this.filterDocuments();
      },
      error => {
        this.notifyService.showNotify(error, 'error');
      },
    );
  }

  private emitPrimaryFacilityUpdatedTrigger() {
    if (this.context !== 'facility' || !this.facilityId) {
      this.sessionService.primaryFacilityUpdatedTrigger.next(true);
      return;
    }
    this.pharmacyService.getPharmacy(this.facilityId).subscribe(
      result => {
        this.sessionService.primaryFacilityUpdatedTrigger.next(true);
      },
      error => {
        this.sessionService.primaryFacilityUpdatedTrigger.next(true);
      },
    );
  }

  documentURL(url): SafeResourceUrl {
    if (url) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    return null;
  }

  filterDocuments() {
    this.filteredDocuments = this.documents.filter(
      document => document.type === this.type,
    );
    // Listing documents are a collection, so show all of them — truncating the
    // list left the extras invisible AND undeletable. Licence contexts keep the
    // single-document view they were built around.
    if (this.limit && this.context !== 'listing') {
      this.filteredDocuments = this.filteredDocuments.slice(0, this.limit);
    }
    this.countChange.emit(this.filteredDocuments.length);
    return this.filteredDocuments;
  }

  get limitReached(): boolean {
    return !!this.limit && this.filteredDocuments.length >= this.limit;
  }

  /**
   * Only worth saying for a bucket that holds several documents. A cap of one is a
   * single-slot field — replace or delete it — so "limit of 1 reached" reads as a
   * problem where it's the normal state. Licence uploaders all run at a limit of 1.
   */
  get showLimitNotice(): boolean {
    return this.limitReached
      && this.limit > 1
      && !this.disableUpload
      && !this.disabled
      && !this.persistUploadButton;
  }

  documentCurrentDelete() {
    this.askDeleteVisible = false;
    this.documentService.delete(this.selectedDocument.id).subscribe(
      (response: any) => {
        if (['Calibration_File'].includes(response.data.type)) {
          this.notifyService.showNotify('History File was deleted.', 'success');
        } else {
          this.notifyService.showNotify('Document was deleted.', 'success');
        }
        this.getDocuments();
        this.update.emit(true);
      },
      error => {
        this.notifyService.showNotify(error, 'error');
      },
    );
  }

  documentOpen = document => window.open(document.url, '_blank').focus();

  sanitizeFileName(name: string) {
    // spaces to dashes
    let sanitized = name.replace(/\s+/g, '-');
    // remove special characters
    sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '');
    sanitized = sanitized.toLowerCase();
    return sanitized;
  }

  private isAllowedFileType(fileName: string): boolean {
    const extension = `.${fileName.split('.').pop()}`;
    return this.allowedFileExtensionsAllCases.includes(extension.toLowerCase());
  }

  private unsupportedTypeMessage(fileNames: string[]): string {
    const allowed = this.allowedFileExtensionsAllCases.join(', ');
    return `Not a supported file type: ${fileNames.join(', ')}. Allowed: ${allowed}`;
  }

  /**
   * Validate and enqueue a dropped/selected batch, then upload sequentially.
   * Slots are reserved up front: the per-file limit check can't be trusted mid-batch
   * because the document list only refreshes once the queue drains.
   */
  private queueFiles(files: File[]) {
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const file of files) {
      if (this.isAllowedFileType(file.name)) {
        accepted.push(file);
      } else {
        rejected.push(file.name);
      }
    }

    const openSlots = this.limit
      ? Math.max(0, this.limit - this.filteredDocuments.length)
      : accepted.length;
    const overflow = accepted.splice(openSlots);

    if (rejected.length) {
      this.notifyService.showNotify(this.unsupportedTypeMessage(rejected), 'error');
    }
    if (overflow.length) {
      this.notifyService.showNotify(
        `${overflow.length} file(s) skipped — limit of ${this.limit} reached`,
        'warning',
      );
    }
    if (!accepted.length) {
      return;
    }

    this.uploadQueue = accepted;
    this.batch = { total: accepted.length, uploaded: 0 };
    this.uploadNextQueuedFile();
  }

  private uploadNextQueuedFile() {
    this.filesToUpload = [this.uploadQueue.shift()];
    this.uploadFile();
  }

  uploadFile() {
    if (!this.filesToUpload.length) {
      return;
    }

    if (!this.isAllowedFileType(this.filesToUpload[0].name)) {
      this.notifyService.showNotify(
        this.unsupportedTypeMessage([this.filesToUpload[0].name]),
        'error',
      );
      this.filesToUpload = [];
      return;
    }

    const formData = new FormData();
    const sanitizedFileName = this.sanitizeFileName(this.filesToUpload[0].name);

    var params = {};
    if (this.context === 'listing' && this.type !== 'global_listing') {
      params['listing_id'] = this.listingId;
      // Without this the row is stored with a null company_id and every later
      // ownership check on it fails — deleting the file returned "access denied".
      params['company_id'] = this.companyId;
    } else if (this.context === 'profile') {
      params['company_id'] = this.companyId;
    } else if (this.context === 'order') {
      params['order_id'] = this.orderId;
    } else if (this.context === 'derma') {
      params['email'] = this.email;
      params['product'] = 'scarpack';
    } else if (
      this.context === 'facility' &&
      [
        'state_license',
        'reseller_certificate',
        'Calibration_File',
        'terms_pdf',
        'dea_license',
      ].includes(this.type)
    ) {
      params['facility_id'] = this.facilityId;
      params['company_id'] = this.companyId;
      params['state_license'] = this.stateLicense;
      params['dea_license'] = this.deaLicense;
      params['update_license_in_db'] = this.updateLicenseinDB;
    } else if (
      this.context === 'facility' &&
      this.type !== 'terms_pdf' &&
      'Calibration_File' &&
      'state_license'
    ) {
      params['facility_id'] = this.facilityId;
    } else if (this.context === 'company' && this.type === 'open_document') {
      params['company_id'] = this.companyId;
    } else if (this.context === 'listing' && this.type === 'global_listing') {
      params['company_id'] = this.companyId;
    }
    params['context'] = this.context;
    params['type'] = this.type;
    formData.append('params', JSON.stringify(params));
    formData.append('doc', this.filesToUpload[0], sanitizedFileName);

    this.isProcessing = true;
    this.progressValue = 0;
    this.processMessage = 'Please wait while your upload is processed';
    this.canCloseProcessing = false;

    // Simulate progress over 20 seconds
    const totalDuration = 20000; // 20 seconds
    const intervalTime = 100; // update every 100ms
    const step = 100 / (totalDuration / intervalTime);

    this.progressInterval = setInterval(() => {
      if (this.progressValue < 95) { // Cap at 95% until done
        this.progressValue += step;
      }
    }, intervalTime);

    this.apiTimeout = setTimeout(() => {
      clearInterval(this.progressInterval);
      this.progressValue = 100;
      this.processMessage = 'The license was not able to be automatically processed.';
      this.canCloseProcessing = true;
    }, totalDuration);

    this.documentService.upload(formData).subscribe(
      (response: any) => {
        // Clear timeout and interval on response
        clearTimeout(this.apiTimeout);
        clearInterval(this.progressInterval);
        this.progressValue = 100;
        this.canCloseProcessing = true;

        this.filesToUpload = [];
        this.batch.uploaded += 1;

        // Licence types are single-document, so they never queue — keep their
        // existing per-upload refresh and AI-parse messaging untouched.
        if (this.uploadQueue.length && !['state_license', 'dea_license'].includes(this.type)) {
          this.processMessage = `Uploading ${this.batch.uploaded + 1} of ${this.batch.total}…`;
          this.uploadNextQueuedFile();
          return;
        }

        this.getDocuments();

        if (['state_license', 'dea_license'].includes(this.type)) {
          const parseResponse = response.data?.ai_parse_response?.data;

          if (parseResponse?.valid) {
            if (parseResponse.updated_state_license_expiration || parseResponse.updated_dea_license_expiration) {
              const newExpiry = parseResponse.updated_state_license_expiration || parseResponse.updated_dea_license_expiration;
              this.processMessage = `License verified and expiration date updated to ${newExpiry}.`;
              this.update.emit(true);
            } else if (parseResponse.existing_license_number_mismatch) {
              this.processMessage = `License verified but the license number does not match our records. Manual verification required.`;
              this.update.emit(true);
            } else if (parseResponse.facility_and_license_address_mismatch) {
              this.processMessage = `License verified but the address does not match our records. This will be manually reviewed within 48 hours.`;
              this.update.emit(true);
            } else {
              this.processMessage = `License uploaded and verified.`;
              this.update.emit(true);
            }
            this.posthogService.capture('license_uploaded', {
              valid: true,
              message: this.processMessage,
            });
          } else if (parseResponse && !parseResponse.valid) {
            this.processMessage = `License could not be automatically verified: ${parseResponse.reason || 'Unknown reason'}. It has been queued for manual review.`;
            this.update.emit(true);
            this.posthogService.capture('license_uploaded', {
              valid: false,
              rejection_reason: parseResponse.reason || 'Unknown reason',
              message: this.processMessage,
            });
          } else {
            this.processMessage = 'License has been uploaded and will be processed within the next 48 hours.';
            this.update.emit(true);
            this.posthogService.capture('license_uploaded', {
              valid: false,
              rejection_reason: 'queued_for_manual_review',
              message: this.processMessage,
            });
          }
          this.emitPrimaryFacilityUpdatedTrigger();
        } else if (['Calibration_File'].includes(this.type)){
          this.processMessage = 'History File Successfully Uploaded.';
          this.update.emit(true);
        } else {
          this.processMessage = this.batch.total > 1
            ? `${this.batch.uploaded} of ${this.batch.total} documents uploaded successfully.`
            : 'Document was uploaded successfully.';
          this.update.emit(true);
        }
      },
      error => {
        clearTimeout(this.apiTimeout);
        clearInterval(this.progressInterval);
        this.progressValue = 100;
        this.canCloseProcessing = true;
        // Abandon the rest of the batch rather than leaving a stalled queue that
        // would resume on the next drop.
        const abandoned = this.uploadQueue.length;
        this.uploadQueue = [];
        this.filesToUpload = [];
        this.processMessage = abandoned
          ? `Upload failed after ${this.batch.uploaded} of ${this.batch.total} — ${abandoned} not attempted.`
          : 'An error occurred during upload.';
        if (this.batch.uploaded) {
          this.getDocuments();
          this.update.emit(true);
        }
        this.notifyService.showNotify(error, 'error');
      },
    );
  }

  closeProcessingModal() {
    this.isProcessing = false;
    this.progressValue = 0;
    this.processMessage = '';
    this.canCloseProcessing = false;
    this.licenseModalClosed.emit(true);
  }

  askDelete(document) {
    this.selectedDocument = document;
    this.askDeleteVisible = true;
  }

  noClick() {
    this.askDeleteVisible = false;
  }

  // ── Drag-and-Drop Handlers ──────────────────
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.queueFiles(Array.from(files));
    }
  }

  onNativeFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const files = Array.from(input.files);
      // Reset so the same file can be re-selected next time
      input.value = '';
      this.queueFiles(files);
    }
  }

  triggerFileSelect() {
    this.nativeFileInput?.nativeElement?.click();
  }
}

@NgModule({
  imports: [
    CommonModule,
    DxFileUploaderModule,
    DxButtonModule,
    DxPopupModule,
    DxProgressBarModule,
    MsTooltipModule,
    RouterModule,
  ],
  declarations: [DocumentUploadComponent],
  exports: [DocumentUploadComponent],
})
export class DocumentUploadModule {}
